/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {
  GenericObject,
  JobTaskProgress
} from '@ion/core/src/types/entities';

/**
 * Defines the Auth Config which is used when retrieving the temporary token
 * used for initiating the underlying WebSocket handshake.
 * `appKeyHeader` defaults to `x-app-key`.
 */
export type PreAuthTokenConfig = {
  websocketTokenURL: string,
  appKey: string,
  appKeyHeader?: string
}

/**
 * Defines the  Auth Token Response which provides the temporary authentication
 * token which is to be used when initiating the underlying WebSocket handshake.
 */
export type PreAuthTokenResponse = {
  response: {
    errors: [],
    results: [{
      authToken: string
    }]
  }
}

/**
 * Defines the default Socket interface which establishes the public
 * API available to all socket clients.
 */
export interface SocketConnection {
  get isConnected(): boolean;
  initialize(options: SocketConfiguration, force?: boolean): void;
  subscribe: Subscription
  unsubscribe: Subscription
  _destroy: () => void;
}

/**
 * Defines the instantiation options required by SocketConnection instances.
 */
export interface SocketConfiguration {
  url: string;
  authToken: string;
  preauthToken?: string,
  appKey: string;
  reconnectionInterval?: number;
  onConnect?: (event: Event) => void;
  onDisconnect?: (event: CloseEvent) => void;
  onConnectionError?: (error: ErrorEvent) => void;
  onData?: OnDataEvent;
  onReconnection?: (attempt: number) => void;
  onSubscription?: (results: SubscriptionResults) => void;
  disableAutoReconnect?: boolean;
  disableKeepAlive?: boolean;
}

/**
 * Defines the WebSocket subscription types used for registering and
 * un-registering subscriptions to and from the underlying service.
 */
export enum SubscriptionAction {
  subscribe = 'subscribe',
  unsubscribe = 'unsubscribe'
}

/**
 * Defines the types of Entity's which can be subscribed to, currently this
 * includes Assets, Tasks (bulk operations), and the Job progress and status
 * channels.
 *
 * Note, the value of each member is sent over the wire as the name of the
 * entity within a subscription message (`data.entities[].name`), and is thus
 * dictated by the underlying service rather than by this module.
 *
 * @see Socket.subscribe
 */
export enum EntitySubscription {
  asset = 'asset',
  task = 'task',
  jobProgress = 'jobProgress',
  jobStatus = 'jobStatus'
}

/**
 * Defines the mapping of the top-level discriminator key of an inbound change
 * message (e.g. `assetStateChange`) to the EntitySubscription under which the
 * observers of that change are registered.
 *
 * @see EntityDescriptor
 */
export const DISCRIMINATOR_ENTITIES: Readonly<Record<string, EntitySubscription>> = {
  assetStateChange  : EntitySubscription.asset,
  taskStateChange   : EntitySubscription.task,
  jobProgressChange : EntitySubscription.jobProgress,
  jobStatusChange   : EntitySubscription.jobStatus
};

/**
 * Defines the valid observable entity identifier types, which in some cases
 * are of type number, while in others they are of type string.
 */
export type ObservableEntityId = number | string;

/**
 * Defines the valid observable entity identifier for individual or multiple
 * ids.
 */
export type ObservableEntityIds = ObservableEntityId | ObservableEntityId[];

/**
 * Defines the routing information resolved from an inbound entity change
 * message, which consists of the message's top-level discriminator, the id
 * of the entity to which the change applies, and the `EntitySubscription`
 * under which the observers of the change are registered.
 *
 * Note, the `id` is normalized to a string when resolved from the message.
 *
 * @see DISCRIMINATOR_ENTITIES
 */
export type EntityDescriptor = {
  discriminator: string,
  id?: string,
  entity?: EntitySubscription
}

/**
 * Defines the Entity Subscription Result type which provides a list of
 * successfully added observer entity ids, and failed entity ids (if any).
 *
 * This type is generally only used internally by SocketConnection instances,
 * and a SubscriptionResults object is then created from a SubscriptionResponse
 * and returned to clients.
 *
 * @see SubscriptionResponse
 */
export type SubscriptionResponse = {
  message: {
    [id in EntitySubscription]?: {
      results: {
        success: {
          ids: ObservableEntityId[]
        }
      },
      errors: {
        failure: {
          ids: ObservableEntityId[]
        }
      }
    }
  } | string
}

/**
 * Defines the consolidated data type parsed and returned from a received
 * `SubscriptionResponse`.
 */
export type SubscriptionResults = {
  succeeded: ObservableEntityId[],
  failed: ObservableEntityId[],
  entity: EntitySubscription,
}

/**
 * Defines the subscription function type used by both the subscribe and
 * unsubscribe methods of a Socket instance.
 */
export type Subscription = (
  entity: EntitySubscription, ids: ObservableEntityIds) => boolean;

/**
 * Defines the Function type definition for an `onData` callback, whereby the
 * data argument is the parsed JSON message received.
 *
 * The onDataEvent can be invoked with either an InboundMessage (change to an
 * entity), or with a SubscriptionResponse (containing the results of adding
 * or removing an entity subscription).
 */
export type OnDataEvent = (
  data: GenericObject, id?: string) => void;

/**
 * Defines the entity agnostic observer API which every entity specific
 * observer exposes; i.e. the methods produced by `createEntityObserver` and
 * delegated to by the AssetObserver, TaskObserver, JobProgressObserver, and
 * JobStatusObserver APIs.
 *
 * Note, entity specific members (such as the `update` utilities of the
 * AssetObserver and TaskObserver) are intentionally excluded, as they are
 * declared by each individual API rather than shared.
 *
 * @see createEntityObserver
 */
export interface EntityObserverApi {
  addObserver: (ids: ObservableEntityIds, observer: OnDataEvent) => boolean;
  removeObserver: (ids: ObservableEntityIds, observer: OnDataEvent) => boolean;
  removeAllObservers: () => boolean;
}

/**
 * Defines the connection state of the underlying WebSocket instance.
 *
 *  [0] 'CONNECTING' : Socket has been created. The connection is not yet open.
 *  [1] 'OPEN'       : The connection is open and ready to communicate.
 *  [2] 'CLOSING'    : The connection is in the process of closing.
 *  [3] 'CLOSED'     : The connection is closed or couldn't be opened.
 */
export enum ConnectionStatus {
  'CONNECTING',
  'OPEN',
  'CLOSING',
  'CLOSED'
}

/**
 * Defines the Observer Map type definition whereby each id has a collection
 * of OnDataEvent function associations which will be invoked upon receiving
 * a message for the given id.
 */
export type ObserverMap = {
  [entity in EntitySubscription]: {
    [id in ObservableEntityId]: OnDataEvent[];
  }
}

/**
 * Defines the state change for an observable entity incoming change event
 * message.
 */
export interface StateChange {
  source: string,
  data: {
    label: string,
    value: string,
    units?: string,
    source: string,
    deviceId: string
  }
}

/**
 * Defines the type for entity update messages received by the underlying
 * WebSocket Connect. Updates to the form of an entity attribute name as
 * the key, with the value being an object containing a source and data
 * property.
 *
 * The data property contains information about the change; generally, the
 * current value of the change, and in some cases, an object containing a
 * value property (which, as the name implies, contains the value) as well
 * as additional information unique to the given attribute.
 *
 * The source property represents the source which triggered the change.
 *
 * ```typescript
 * {
 *   "change": {
 *     "latitude" : {
 *       "source" : 'MessagePrecedence'
 *       "data" : {
 *         "label"    : 'latitude',
 *         "value"    : '50.5569135',
 *         "units"    : null,
 *         "source"   : 'LMU',
 *         "deviceId" : '420343'
 *       },
 *     },
 *     "odometer": {
 *       "source": "MessagePrecedence",
 *       "data" : {
 *         "label"  : "Odometer",
 *         "value"  : "4621.15",
 *         "units"  : "mi",
 *         "source" : "OEM"
 *       }
 *     }
 *  }
 * ```
 */
export interface EntityUpdate {
  [key: string]: StateChange | any;
}

/**
 * Defines the message type for an incoming Asset Change, which implements
 * the standard EntityMessage with additional properties unique to Assets,
 * such as `assetId`, etc.
 */
export interface AssetMessage {
  assetStateChange: {
    assetId: number
    lastMessageTime: number
    lastMessageDeviceType: string
    change: EntityUpdate
  }
}

/**
 * Defines the device counts of a single Job task, which are always provided
 * in their entirety rather than as a delta.
 */
export interface JobTaskCounts {
  total: number,
  completed: number,
  pending: number,
  failed: number,
  cancelled: number
}

/**
 * Defines the change received for a single Job task, of which a
 * `jobProgressChange` message carries one entry per changed task.
 *
 * Note, the values of a task change are provided directly rather than
 * wrapped, as the collection of task changes is itself a single attribute of
 * the change, and is thus unwrapped in its entirety.
 *
 * Note, only the `taskIndex` is guaranteed, as a change carries the attributes
 * of the task which changed rather than the task in full. The `taskIndex` is
 * the position of the task within the Job, and is stable for its life.
 *
 * @see JobProgressUpdate
 */
export interface JobTaskProgressPatch {
  taskIndex: number,
  version?: number,
  lastModifiedOn?: string,
  progress?: JobTaskProgress,
  counts?: JobTaskCounts
}

/**
 * Defines the consolidated updates of a `jobProgressChange` message, as they
 * are received by an observer; i.e. once each attribute of the change has
 * been unwrapped.
 *
 * The `lastModifiedOn` of the Job row orders the Job scope of a change against
 * that of another, and against the response of a fetch; that of a task change
 * orders only that task. The `version` is that of the Job row and is carried
 * for diagnosis only — it orders nothing, as the CTC list response cannot
 * return one to compare it against.
 *
 * Note, every attribute is optional, as an unwrapped change carries only those
 * the message itself carried; a consumer must therefore never assume a key is
 * present. A scope of a change which carries no timestamp is thus unorderable,
 * and is never applied.
 *
 * @see JobProgressMessage
 */
export interface JobProgressUpdate {
  progress?: JobTaskProgress,
  version?: number,
  lastModifiedOn?: string,
  tasks?: JobTaskProgressPatch[]
}

/**
 * Defines the consolidated updates of a `jobStatusChange` message, as they
 * are received by an observer. Emitted only once a Job has reached a terminal
 * status, and carries every attribute required to describe that outcome.
 *
 * Note, the `version` of the Job row is carried by the message but is
 * intentionally absent here; as the change is emitted at most once per Job
 * lifecycle, there is no second change against which to order it.
 *
 * Note also that every attribute is optional, as an unwrapped change carries
 * only those the message itself carried; a consumer must therefore never
 * assume a key is present.
 *
 * @see JobStatusMessage
 */
export interface JobStatusUpdate {
  name?: string,
  lastModifiedOn?: string,
  progress?: JobTaskProgress
}

/**
 * Defines the message type for an incoming Job progress change, which is
 * received for every task status or device count change of an observed Job.
 *
 * ```typescript
 *
 * {
 *   "message": {
 *     "jobProgressChange": {
 *       "id": 12345,
 *       "change": {
 *         "progress"       : {"data": {"value": {"status": "InProgress"}}},
 *         "version"        : {"data": {"value": 4}},
 *         "lastModifiedOn" : {"data": {"value": "2026-08-13T15:33:59.772Z"}},
 *         "tasks"          : {"data": {"value": [{
 *           "taskIndex"      : 2,
 *           "version"        : 2,
 *           "lastModifiedOn" : "2026-08-13T15:33:59.772Z",
 *           "progress"       : {"status": "Failed", "reason": "..."},
 *           "counts"         : {"total": 3, "completed": 0, "pending": 0,
 *                               "failed": 3, "cancelled": 0}
 *         }]}}
 *       }
 *     }
 *   }
 * }
 * ```
 */
export interface JobProgressMessage {
  jobProgressChange: {
    id: number,
    change: EntityUpdate
  }
}

/**
 * Defines the message type for an incoming Job status change, which is
 * received once an observed Job has reached a terminal status.
 *
 * ```typescript
 *
 * {
 *   "message": {
 *     "jobStatusChange": {
 *       "id": 12345,
 *       "change": {
 *         "name"           : {"data": {"value": "Provision 40 devices"}},
 *         "lastModifiedOn" : {"data": {"value": "2026-08-13T15:34:05.120Z"}},
 *         "version"        : {"data": {"value": 6}},
 *         "progress"       : {"data": {"value": {
 *           "status": "Failed", "reason": "MAX_RETRIES_REACHED"
 *         }}}
 *       }
 *     }
 *   }
 * }
 * ```
 */
export interface JobStatusMessage {
  jobStatusChange: {
    id: number,
    change: EntityUpdate
  }
}

/**
 * Defines the type for an inbound message from the underlying WebSocket
 * Service API.
 *
 * An inbound message can consist of an object which contains a top-level
 * entity state change key (e.g. "assetStateChange"), which contains the
 * id of the entity, the `lastMessageTime`, and a `change` property. The
 * change property provides top-level keys representing each attribute
 * that has changed. The value changes are generally provided under the
 * `data.value` property, though, in some cases, the `data` property will
 * represent the change in value.
 *
 * ```typescript
 *
 * {
 *   "message": {
 *     "assetStateChange": {
 *       "assetId": 395744,
 *       "lastMessageDeviceType": 'LMU',
 *       "lastMessageTime": 1643291190712,
 *       "change": {
 *         "latitude" : {
 *           "source" : 'MessagePrecedence',
 *           "data" : {
 *             "label"    : 'latitude',
 *             "value"    : '50.5569135',
 *             "units"    : null,
 *             "source"   : 'LMU',
 *             "deviceId" : '420343'
 *           }
 *         },
 *         "odometer": {
 *           "source": "MessagePrecedence",
 *           "data" : {
 *             "label"  : "Odometer",
 *             "value"  : "4621.15",
 *             "units"  : "mi",
 *             "source" : "OEM"
 *           }
 *         },
 *         "dtcCodes" : {
 *           "source" : "OEM_DTC",
 *           "data" : [{
 *             "dtcCode"         : "bmw_CCM_143",
 *             "description"     : "Tyre pressure loss. Caution stop Status: ZERO Distance Remaining: 105592",
 *             "type"            : null,
 *             "typeDescription" : "OEM:BMW"
 *           }],
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 */
export interface InboundMessage {
  message: AssetMessage | JobProgressMessage | JobStatusMessage | SubscriptionResponse
}
